import React, { ChangeEvent } from 'react';

import { StaffAllocationsPanel } from '.';

import { Event } from '../../shared/types/event';
import { PartialEvent, partialEventToEvent } from '../lib/event';
import { EventTemplate, EventDisplayOption } from '../../shared/types/eventTemplate';

import { VenueTemplate } from '../../shared/types/venueTemplate';
import { Plan } from '../../shared/types/plan';
import { TimeBlock, Time } from '../../shared/types/time';
import { blockToTime } from '../../shared/lib/grid';
import {
  timeToMinutes,
  stringToTime,
  timeToString,
  addMinutesToTime,
  DEFAULT_TIMESTR_FORMAT,
} from '@go-shared/lib/time';
import Config from '../lib/config';

import { makeItem, TypeaheadItem } from '@spark-labs/avocado-ui/dist/components/Typeahead';
import { InputElement } from '@spark-labs/avocado-ui/dist/components/Input';
import {
  Alert,
  RadioButton,
  Typeahead,
  Button,
  RadioButtonGroup,
  FormGroup,
  Label,
  Wizard,
  Header,
  Input,
} from '@spark-labs/avocado-ui';
import './EventForm.scss';
import { HourFormat } from '../../shared/types/brand';
import { StaffTemplate } from '../../shared/types/staffTemplate';
import { StaffAllocation, PartialStaffAllocation } from '../../shared/types/staffAllocation';
import { isValidEventDisplayOption } from '@go-shared/lib/event';
import { TimeInput } from './TimeInput';
import { getPropertyConfiguration } from '@go-shared/lib/propertyConfig';
import { create, populate } from '@go-client/lib/api/eventTemplate';
import { generateDefaultNewEventTemplate } from '@go-shared/lib/eventTemplate';
import { toast } from './UIkit/Toast/Toast';
import { getServerInfo } from '@go-client/lib/api/info';
import { parseLoggedUserToken } from '@go-client/lib/auth';
import { getDayLocation } from '@go-shared/lib/sailing';
import { BareSailingDay } from '@go-shared/types/sailing';
import { getErrorMessage } from '@go-shared/errors';
import { safeIdCompare } from '@go-shared/lib/utils';
import { canManageLibraries } from '@go-shared/lib/permissions';
import { GridLocationData } from './Grid';
import { getHourFormat } from '@go-shared/lib/property';

interface EventFormDayTransfer {
  current: BareSailingDay;
  options: BareSailingDay[];
}

interface EventFormMode {
  type: 'create' | 'edit';
  event: PartialEvent;
  initialPage?: number;
  dayTransfer?: EventFormDayTransfer;
  gridLoc?: GridLocationData;
}

interface EventFormProps {
  mode: EventFormMode;
  eventTemplates: EventTemplate[];
  venueTemplates: VenueTemplate[];
  staffTemplates: StaffTemplate[];
  plan: Plan;
  onSubmit?: (event: Event, applyStaffToPlan?: boolean, transferToTargetDay?: string) => void;
  onEventDelete?: (event: PartialEvent) => void;
  onEventTemplateCreated: (newEventTemplate: EventTemplate) => void;
  blockLength?: number;
  property: string;
}

interface EventFormState {
  editedEvent: PartialEvent;
  currentPage: number;
  applyStaffAllocationsToPlan: string;
  titleValue: string;
  sailingDayValue?: string;
  createNewEvent: boolean;
  showNewEventBtn: boolean;
  allowTemplateChange: boolean;
}

/**
 * Given a sailing day, return an item that can be used in a Typeahead
 *
 */
function getSailingDayString(day: BareSailingDay): string {
  let dayLocation = getDayLocation(day);
  return `Day ${day.name} - ${dayLocation}`;
}

function makeSailingDayItem(day: BareSailingDay): { key: string; element: string } {
  return makeItem(day._id, getSailingDayString(day));
}

export default class EventForm extends React.Component<EventFormProps, EventFormState> {
  public static readonly STAFF_ALLOCATION_PAGE = 1;

  public readonly state: EventFormState;

  private blockLength: number;
  private dayStart: number;
  private hourFormat: HourFormat;

  constructor(props: EventFormProps) {
    super(props);

    this.state = {
      editedEvent: props.mode.event,
      currentPage: props.mode.initialPage || 0,
      applyStaffAllocationsToPlan: 'single',
      titleValue: props.mode.event.template ? props.mode.event.template.title : '',
      createNewEvent: false,
      showNewEventBtn: false,
      sailingDayValue: '',
      allowTemplateChange: true,
    };

    this.blockLength = this.props.blockLength || Config.DEFAULT_BLOCK_LENGTH;
    this.dayStart = timeToMinutes(this.props.plan.dayStart);
    this.hourFormat = getHourFormat(this.props.plan.property);

    this.getDefaultStaffAllocations = this.getDefaultStaffAllocations.bind(this);
  }

  public async componentDidMount() {
    let info = await getServerInfo();

    const showNewEventBtn = canManageLibraries(parseLoggedUserToken());

    this.setState({
      showNewEventBtn,
      allowTemplateChange: info.settings.ALLOW_EVENT_TEMPLATE_CHANGE,
    });
  }

  public render() {
    let event = this.state.editedEvent;

    // This is a combination of all unarchived events
    // and the currently active event (even if it is archived)
    let filteredTemplates = this.props.eventTemplates.filter(
      et => safeIdCompare(et._id, event.template?._id) || !et.archived
    );

    let templates = filteredTemplates.map(et => makeItem(et._id, et.title));

    let venues = this.props.venueTemplates.map(vt => makeItem(vt._id, vt.name));
    let days =
      this.props.mode.type === 'edit' && this.props.mode.dayTransfer
        ? this.props.mode.dayTransfer.options.map(makeSailingDayItem)
        : [];

    let startTime = event.startTime
      ? timeToString(event.startTime, this.hourFormat, DEFAULT_TIMESTR_FORMAT)
      : undefined;

    let endTime = event.endTime
      ? timeToString(event.endTime, this.hourFormat, DEFAULT_TIMESTR_FORMAT)
      : undefined;

    let interactiveFeedDisplay: EventDisplayOption | undefined;
    if (event.interactiveFeedDisplay) {
      interactiveFeedDisplay = event.interactiveFeedDisplay;
    } else if (event.template) {
      interactiveFeedDisplay = event.template.interactiveFeedDisplay;
    } else {
      interactiveFeedDisplay = undefined;
    }

    let deleteButton =
      this.props.mode.type === 'edit' ? (
        <Button color="red" onClick={this.handleEventDelete}>
          Delete
        </Button>
      ) : undefined;

    let staffAllocationsData = this.getStaffAllocationsData(
      this.props.plan.dayStart,
      this.hourFormat,
      this.state.editedEvent
    );

    let levelLabel = getPropertyConfiguration(this.props.plan.property.type, 'levelLabel');
    let showDDPOptions = getPropertyConfiguration(
      this.props.plan.property.type,
      'showDigitalDailyProgramOptions'
    );

    // Only allow changing the template if we are in create mode, or in edit mode and the server
    // settings allow it
    let allowTemplateChange = this.props.mode.type === 'create' || this.state.allowTemplateChange;

    const shouldAllowNewItem = () => {
      return (
        this.state.showNewEventBtn &&
        !templates.find(
          template => template.element.toLowerCase() === this.state.titleValue.toLowerCase()
        )
      );
    };
    return (
      <div className="border-t border-grey pt-2 p-4 mt-6" style={{ width: '800px' }}>
        <Wizard
          currentPage={this.state.currentPage}
          onCurrentPageChange={this.handleCurrentPageChange}
          onSubmit={this.handleFormSubmit}
          extraButtons={deleteButton}
        >
          <div>
            <div className="flex flex-col">
              <div className="flex flex-wrap">
                <FormGroup className="w-1/2 py-2 px-4">
                  {allowTemplateChange ? (
                    <Typeahead
                      label="Title"
                      inputClassName={
                        !this.state.createNewEvent &&
                        event.template &&
                        this.state.titleValue !== event.template.title
                          ? 'text-red'
                          : undefined
                      }
                      value={this.state.titleValue}
                      items={templates}
                      allowNewItems={shouldAllowNewItem()}
                      onChange={e => {
                        this.setState({ titleValue: e.target.value });
                      }}
                      onSelectNew={this.handleShouldCreateNewEvent}
                      onSelect={this.handleTemplateChange}
                      newValueLabel="(Create new event)"
                    />
                  ) : (
                    <Input label="Title" readOnly value={this.state.titleValue} />
                  )}
                </FormGroup>
                {this.props.mode.type === 'edit' && this.props.mode.dayTransfer && (
                  <FormGroup className="w-1/2 py-2 px-4">
                    <Typeahead
                      label="Sailing Day"
                      value={getSailingDayString(this.props.mode.dayTransfer.current)}
                      items={days}
                      onSelect={this.handleSailingDayChange}
                    />
                  </FormGroup>
                )}
              </div>
              <div className="flex flex-wrap">
                <FormGroup className="w-1/2 py-2 px-4">
                  <TimeInput
                    label="Start Time"
                    value={startTime}
                    timeFormat={this.hourFormat}
                    startTime={this.dayStart}
                    onSelect={this.handleStartTimeSelect}
                    onChange={this.handleStartTimeChange}
                  />
                </FormGroup>
                <FormGroup className="w-1/2 py-2 px-4">
                  <TimeInput
                    label="End Time"
                    value={endTime}
                    timeFormat={this.hourFormat}
                    startTime={this.dayStart}
                    onSelect={this.handleEndTimeSelect}
                    onChange={this.handleEndTimeChange}
                  />
                </FormGroup>
              </div>
              <FormGroup className="w-full py-2 px-4">
                <Typeahead
                  label="Venue"
                  value={event.venue ? event.venue.name : undefined}
                  items={venues}
                  onSelect={this.handleVenueChange}
                />
              </FormGroup>
              {showDDPOptions && (
                <FormGroup className="w-full py-2 px-4">
                  <Input
                    label="Click Through Link"
                    value={event.callToAction}
                    onChange={this.handleCallToActionChange}
                  />
                </FormGroup>
              )}
              <div className="p-4">
                <div>
                  <Header size="sm">Display Options </Header>
                  <FormGroup className="bg-grey-lighter p-4">
                    <div className="mt-4 flex w-full">
                      <div className="w-1/2 flex">
                        <Label text={`Display ${levelLabel} Name As (Lineup)`} />
                        <input
                          className="ml-2"
                          onChange={this.handleDeckDisplayNameChange}
                          value={event.deckDisplayName}
                        />
                      </div>
                      <div className="w-1/2 flex">
                        <Label text="Display Venue Name As (Lineup)" />
                        <input
                          className="ml-2"
                          onChange={this.handleVenueDisplayNameChange}
                          value={event.venueDisplayName}
                        />
                      </div>
                    </div>
                    <div
                      className="mt-4 flex flex-wrap"
                      style={{ justifyContent: 'space-between' }}
                    >
                      <RadioButtonGroup
                        name="interactiveFeedDisplay"
                        onChange={this.handleInteractiveFeedDisplayChange}
                        groupValue={interactiveFeedDisplay}
                      >
                        {groupProps => (
                          <>
                            <RadioButton label="Unassigned" {...groupProps} />
                            <RadioButton
                              label="Display End Time"
                              value={'show-finish-time'}
                              {...groupProps}
                            />
                            <RadioButton
                              label="Do NOT Display End Time"
                              value={'do-not-show-finish-time'}
                              {...groupProps}
                            />
                            <RadioButton
                              label="Display as 24/7"
                              value={'show-as-24/7'}
                              {...groupProps}
                            />
                            <RadioButton label="Ends Late" value={'ends-late'} {...groupProps} />
                          </>
                        )}
                      </RadioButtonGroup>
                    </div>
                  </FormGroup>
                </div>
              </div>
            </div>
          </div>

          {/*  Wizard page 2 */}
          <div>
            {staffAllocationsData && (
              <StaffAllocationsPanel
                eventDuration={staffAllocationsData.eventDuration}
                staffAllocations={staffAllocationsData.staffAllocations}
                relatedEventTime={staffAllocationsData.relatedEventTime}
                staffTemplates={this.props.staffTemplates}
                onChange={this.handleStaffAllocationsChange}
              />
            )}
            <div className="flex justify-end mb-8">
              <div className="mr-3 font-bold text-grey-darker">Save changes for:</div>
              <div>
                <RadioButtonGroup
                  name="apply-to-all"
                  groupValue={this.state.applyStaffAllocationsToPlan}
                  onChange={this.handleStaffAllocationsToPlanChange}
                >
                  {groupProps => (
                    <div className="flex flex-col">
                      <div className="mb-2">
                        <RadioButton {...groupProps} value="single" label="This Occurrence Only" />
                      </div>
                      <div>
                        <RadioButton {...groupProps} value="all" label="All Occurrences" />
                      </div>
                    </div>
                  )}
                </RadioButtonGroup>
              </div>
            </div>
          </div>
        </Wizard>
      </div>
    );
  }

  /**
   * Gets the default staff allocations from a template and populates the staff templates
   *
   */
  private getDefaultStaffAllocations(eventTemplate: EventTemplate): StaffAllocation[] {
    let propertyInfo = eventTemplate.properties.find(
      p => p.property === this.props.plan.property._id
    );
    if (!propertyInfo) return [];

    let defaultStaffAllocations: StaffAllocation[] = [];
    propertyInfo.defaultStaffAllocations.forEach(sa => {
      let template = this.props.staffTemplates.find(t => t._id === sa.template);
      if (template) {
        defaultStaffAllocations.push({
          ...sa,
          template,
        });
      }
    });

    return defaultStaffAllocations;
  }

  private getStaffAllocationsData(dayStart: Time, hourFormat: HourFormat, event?: PartialEvent) {
    if (!event || !event.startTime || !event.endTime) return;

    let relatedEventTime = {
      eventStart: event.startTime,
      dayStart,
      hourFormat,
    };

    let dayStartMinutes = timeToMinutes(dayStart);
    let eventStartMinutes = timeToMinutes(event.startTime, dayStartMinutes);
    let eventEndMinutes = timeToMinutes(event.endTime, dayStartMinutes);

    return {
      eventDuration: eventEndMinutes - eventStartMinutes,
      staffAllocations: event.staffAllocations,
      relatedEventTime,
    };
  }

  /**
   * NOTE: When a new event template is assigned, it will overwrite staff allocations in progress!
   */
  private handleTemplateChange = async (item: TypeaheadItem) => {
    let template = this.props.eventTemplates.find(t => t._id === item.key);
    if (!template) {
      throw new Error(`Could not find template ${item}`);
    }

    // Mark private events as hidden by default
    let hidden = template.isPrivate;
    let interactiveFeedDisplay = template.interactiveFeedDisplay;

    const editedEvent = {
      ...this.state.editedEvent,
      template,
      hidden,
      interactiveFeedDisplay,
      staffAllocations: this.getDefaultStaffAllocations(template),
    };

    if (editedEvent.startTime) {
      editedEvent.endTime = addMinutesToTime(editedEvent.startTime, template.defaultDuration);
    }

    this.setState({
      editedEvent,
      createNewEvent: false,
      titleValue: template.title,
    });
  };

  private handleFormSubmit = async () => {
    let shouldCreateEvent = false;
    if (this.state.createNewEvent) {
      // Event doesn't exists, ask the user if he wants to create it
      shouldCreateEvent = await Alert.question({
        title: `Event Template doesn't exists`,
        message: `${this.state.titleValue} event doesn't exist, do you want to create it?`,
        yesButton: 'Create',
        noButton: 'Cancel',
      });
    }

    // If the user allow the event creation, create a partial event
    if (shouldCreateEvent) {
      await this.handleCreateNewEventTemplate();
    }

    let event;
    try {
      event = partialEventToEvent(this.state.editedEvent, timeToMinutes(this.props.plan.dayStart));
    } catch (e) {
      return Alert.error(getErrorMessage(e));
    }

    if (this.props.onSubmit) {
      this.props.onSubmit(
        event,
        this.state.applyStaffAllocationsToPlan === 'all',
        this.state.sailingDayValue
      );
    }
  };

  private handleEventDelete = () => {
    if (this.props.onEventDelete) {
      this.props.onEventDelete(this.state.editedEvent);
    }
  };

  private handleCurrentPageChange = (newPage: number) => {
    this.setState({ currentPage: newPage });
  };

  private handleVenueDisplayNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    this.setState({
      editedEvent: { ...this.state.editedEvent, venueDisplayName: event.target.value },
    });
  };

  private handleDeckDisplayNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    this.setState({
      editedEvent: { ...this.state.editedEvent, deckDisplayName: event.target.value },
    });
  };

  private handleStartTimeSelect = (startTimeBlock: TimeBlock) => {
    let startTime = blockToTime(startTimeBlock.index, this.blockLength, this.dayStart);
    this.setState({ editedEvent: { ...this.state.editedEvent, startTime } });
  };

  private handleEndTimeSelect = (endTimeBlock: TimeBlock) => {
    let endTime = blockToTime(endTimeBlock.index, this.blockLength, this.dayStart);
    this.setState({ editedEvent: { ...this.state.editedEvent, endTime } });
  };

  private handleStartTimeChange = (event: React.ChangeEvent<InputElement>) => {
    try {
      let startTime = stringToTime(event.target.value);
      this.setState({ editedEvent: { ...this.state.editedEvent, startTime } });
    } catch (_e) {
      // pass
    }
  };

  private handleEndTimeChange = (event: React.ChangeEvent<InputElement>) => {
    try {
      let endTime = stringToTime(event.target.value);
      this.setState({ editedEvent: { ...this.state.editedEvent, endTime } });
    } catch (_e) {
      // pass
    }
  };

  private handleVenueChange = (item: TypeaheadItem) => {
    let venue = this.props.venueTemplates.find(v => v._id === item.key);
    this.setState({ editedEvent: { ...this.state.editedEvent, venue } });
  };

  private handleSailingDayChange = (item: TypeaheadItem) => {
    if (this.props.mode.type !== 'edit' || !this.props.mode.dayTransfer) return;
    let day = this.props.mode.dayTransfer.options.find(sd => sd._id === item.key);
    if (!day) return;
    this.setState({ sailingDayValue: day._id });
  };

  private handleCallToActionChange = (e: ChangeEvent<InputElement>) => {
    this.setState({
      editedEvent: {
        ...this.state.editedEvent,
        callToAction: e.target.value,
      },
    });
  };

  private getInteractiveFeedDisplay(
    interactiveFeedDisplay: string
  ): EventDisplayOption | undefined {
    return isValidEventDisplayOption(interactiveFeedDisplay)
      ? (interactiveFeedDisplay as EventDisplayOption)
      : undefined;
  }

  private handleInteractiveFeedDisplayChange = (interactiveFeedDisplay: string) => {
    if (interactiveFeedDisplay && !isValidEventDisplayOption(interactiveFeedDisplay)) {
      throw new Error('Wrong interactive feed option');
    }

    let displayOption = this.getInteractiveFeedDisplay(interactiveFeedDisplay);
    this.setState({
      editedEvent: {
        ...this.state.editedEvent,
        interactiveFeedDisplay: displayOption,
      },
    });
  };

  private handleStaffAllocationsChange = (staffAllocations: PartialStaffAllocation[]) => {
    this.setState({
      editedEvent: {
        ...this.state.editedEvent,
        staffAllocations: staffAllocations as StaffAllocation[],
      },
    });
  };

  private handleStaffAllocationsToPlanChange = (value: string) => {
    this.setState({ applyStaffAllocationsToPlan: value });
  };

  private handleCreateNewEventTemplate = async () => {
    let template: EventTemplate | undefined;

    const { brand } = this.props.eventTemplates[0];
    const bareNewEventTemplate = await create(
      generateDefaultNewEventTemplate({
        title: this.state.titleValue,
        brandId: brand._id,
        source: this.props.property,
        properties: [{ property: this.props.property, defaultStaffAllocations: [] }],
      })
    );
    if (bareNewEventTemplate) {
      template = populate(bareNewEventTemplate, brand);
      this.props.onEventTemplateCreated(template);
    }

    if (template) {
      await toast.success(`${template.title} event template created correctly`);

      let hidden = template.isPrivate;
      let interactiveFeedDisplay = template.interactiveFeedDisplay;

      this.setState({
        editedEvent: {
          ...this.state.editedEvent,
          template,
          hidden,
          interactiveFeedDisplay,
          staffAllocations: this.getDefaultStaffAllocations(template),
        },
        titleValue: template.title,
      });
    }
  };

  private handleShouldCreateNewEvent = () => {
    this.setState({ createNewEvent: true });
  };
}
